import * as React from 'react';
import difference from 'lodash.difference';
import intersection from 'lodash.intersection';
import { Table, Checkbox, Loader, Segment, Dimmer } from 'semantic-ui-react';
import { FieldInputProps } from 'react-final-form';

// Global Components
// @ts-ignore
import EntityTypeName from 'components/EntityTypeName';
// @ts-ignore
import ResponsiveTable from 'components/ResponsiveTable';
import LoadingGate from 'components/LoadingGate';
import ErrorGate from 'components/ErrorGate';
import ErrorMessage from 'components/ErrorMessage';

// Child Components
import { InnerEntityOptions } from 'scenes/Spray/scenes/Diary/components/SprayEventFinalForm/components/SprayEventForm/components/SpraySchemaInnerEntityTableField/components/SpraySchemaInnerEntityOptions/types';
import { EntityId } from 'services/entities/types';
import { APPLIED_FERTILIZER_ENTITIES_BY_DATE_LOAD_NAMESPACE } from 'services/appliedFertilizerEntitiesByDate/constants';
import { SPRAYED_ENTITIES_BY_DATE_LOAD_NAMESPACE } from 'services/sprayedEntitiesByDate/constants';
import RenderEntityTableRows from './components/RenderEntityTableRows';

interface Props {
    input: FieldInputProps<any, HTMLElement>;
    meta: any; // TypeScript won't compile without this defined
    entityTypeId?: number;
    entityId?: number;
    innerEntitiesGroupedByVariety?: Record<string, InnerEntityOptions>;
    innerEntityIds: EntityId[];
    namespace:
        | typeof APPLIED_FERTILIZER_ENTITIES_BY_DATE_LOAD_NAMESPACE
        | typeof SPRAYED_ENTITIES_BY_DATE_LOAD_NAMESPACE;
}

export default class RenderEntityTable extends React.PureComponent<Props> {
    componentDidMount(): void {
        const {
            input: { onChange, value }
        } = this.props;

        // set default value as number[]
        if (!Array.isArray(value)) onChange([]);
    }

    componentDidUpdate(prevProps: Props): void {
        const {
            input: { onChange },
            entityId
        } = this.props;

        // Reset selection if entityId changes
        if (entityId !== prevProps.entityId) {
            onChange([]);
        }
    }

    get checked(): boolean {
        const {
            input: { value },
            innerEntityIds
        } = this.props;
        if (!innerEntityIds.length) {
            return false;
        }
        return !difference(innerEntityIds, value).length;
    }

    get indeterminate(): boolean {
        const {
            input: { value },
            innerEntityIds
        } = this.props;

        if (this.checked) {
            return false;
        }
        return !!intersection(value, innerEntityIds).length;
    }

    handleChange = (): void => {
        const {
            input: { onChange },
            innerEntityIds
        } = this.props;

        if (!this.checked && !this.indeterminate && innerEntityIds) {
            onChange(innerEntityIds);
        } else {
            onChange([]);
        }
    };

    render(): React.ReactElement | null {
        const {
            input,
            entityTypeId,
            innerEntityIds,
            innerEntitiesGroupedByVariety,
            namespace,
            entityId,
            meta: { invalid, dirtySinceLastSubmit, submitFailed }
        } = this.props;

        if (!entityId) return null;

        return (
            <LoadingGate
                namespace={namespace}
                LoadingComponent={
                    <Segment basic padded="very">
                        <Dimmer active inverted>
                            <Loader inverted />
                        </Dimmer>
                    </Segment>
                }
            >
                <ErrorGate namespace={namespace}>
                    <ResponsiveTable>
                        <Table
                            sortable
                            striped
                            singleLine
                            unstackable
                            size="small"
                        >
                            <Table.Header
                                style={{
                                    fontSize: 14
                                }}
                            >
                                <Table.Row>
                                    <Table.HeaderCell
                                        collapsing
                                        textAlign="center"
                                        verticalAlign="middle"
                                        className="events-table-cell-padding"
                                        style={{
                                            lineHeight: 0,
                                            borderRadius: 0
                                        }}
                                    >
                                        <Checkbox
                                            checked={this.checked}
                                            indeterminate={this.indeterminate}
                                            onChange={this.handleChange}
                                        />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell className="events-table-cell-padding">
                                        <EntityTypeName
                                            entityTypeId={entityTypeId}
                                        />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell className="events-table-cell-padding">
                                        Variety
                                    </Table.HeaderCell>
                                    <Table.HeaderCell className="events-table-cell-padding">
                                        {namespace ===
                                        APPLIED_FERTILIZER_ENTITIES_BY_DATE_LOAD_NAMESPACE
                                            ? 'Applied'
                                            : 'Sprayed'}
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                <RenderEntityTableRows
                                    input={input}
                                    innerEntityIds={innerEntityIds}
                                    innerEntitiesGroupedByVariety={
                                        innerEntitiesGroupedByVariety
                                    }
                                    namespace={namespace}
                                />
                            </Table.Body>
                        </Table>
                    </ResponsiveTable>
                    <ErrorMessage
                        title="You must select at least 1 area"
                        visible={
                            submitFailed && invalid && !dirtySinceLastSubmit
                        }
                    />
                </ErrorGate>
            </LoadingGate>
        );
    }
}
